import { NOTIFY_CUSTOM_LIMITS, type WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Input, Textarea } from "@sagot/ui";
import type { NotifyTeamStep } from "../../types";
import { VariableList } from "../webhook-form";
import { hasStandardMessage, sampleMessage, withOwnMessage } from "./system-model";

const D = "engage.workflows.defaults.standard";
const F = "engage.workflows.fields";

interface NotifyMessageProps {
  step: NotifyTeamStep;
  trigger: WorkflowTrigger | undefined;
  disabled: boolean;
  onChange: (step: NotifyTeamStep) => void;
}

/** The standard message (the wording people always got) with an example, or the workspace's own title and message. */
export function NotifyMessage({ step, trigger, disabled, onChange }: NotifyMessageProps) {
  const { t } = useTranslation();
  const { message } = step;
  if (message.template === "system") {
    const sample = sampleMessage(trigger, t);
    return (
      <section className="space-y-3 rounded-md border border-line p-3" aria-labelledby={`${step.id}-standard`} data-testid="standard-message">
        <div>
          <h3 id={`${step.id}-standard`} className="text-sm font-bold">{t(`${D}.title`)}</h3>
          <p className="text-xs text-fg-subtle">{t(`${D}.body`)}</p>
        </div>
        {sample ? (
          <div className="rounded-md bg-surface-2 p-3" role="group" aria-label={t(`${D}.preview`)}>
            <p className="text-sm font-semibold" data-testid="standard-title">{sample.title}</p>
            {sample.body ? <p className="text-xs text-fg-muted">{sample.body}</p> : null}
          </div>
        ) : null}
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => { onChange(withOwnMessage(step, trigger, t)); }}>
          <Icon name="pencil" />
          {t(`${D}.writeOwn`)}
        </Button>
        <p className="text-xs text-fg-subtle">{t(`${D}.writeOwnHint`)}</p>
      </section>
    );
  }
  return (
    <section className="space-y-3 rounded-md border border-line p-3" aria-labelledby={`${step.id}-custom`}>
      <h3 id={`${step.id}-custom`} className="text-sm font-bold">{t(`${D}.custom`)}</h3>
      <Field label={t(`${F}.title`)}>
        <Input maxLength={NOTIFY_CUSTOM_LIMITS.title} disabled={disabled} value={message.title} onChange={(e) => { onChange({ ...step, message: { ...message, title: e.target.value } }); }} />
      </Field>
      <Field label={t(`${F}.notifyBody`)}>
        <Textarea rows={3} maxLength={NOTIFY_CUSTOM_LIMITS.body} disabled={disabled} value={message.body ?? ""} onChange={(e) => { onChange({ ...step, message: { template: "custom", title: message.title, ...(e.target.value ? { body: e.target.value } : {}) } }); }} />
      </Field>
      <VariableList />
      {hasStandardMessage(trigger) ? (
        <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => { onChange({ ...step, message: { template: "system" } }); }}>
          <Icon name="rotate-ccw" />
          {t(`${D}.useStandard`)}
        </Button>
      ) : (
        <p className="text-xs text-fg-subtle">{t(`${D}.noStandard`)}</p>
      )}
    </section>
  );
}
